import React, { useState, useEffect, useRef } from 'react';
import { Phone, PhoneOff, Mic, MicOff, X, Loader2, Wifi, WifiOff } from 'lucide-react';

const PLIVO_SDK_URL = 'https://cdn.plivo.com/sdk/browser/v2/plivo.min.js';

function CallModal({ isOpen, onClose, phoneNumber, personName, personId, apiService }) {
  const [phase, setPhase] = useState('confirm'); // confirm, connecting, ready, calling, ended
  const [callStatus, setCallStatus] = useState(null); // initiated, ringing, answered, completed, failed
  const [error, setError] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [sdkConnected, setSdkConnected] = useState(false);
  const [registeredCallId, setRegisteredCallId] = useState(null);
  const plivoRef = useRef(null);

  // Format phone for display
  const formatDisplayPhone = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length >= 10) {
      const last10 = digits.slice(-10);
      return `(${last10.slice(0, 3)}) ${last10.slice(3, 6)}-${last10.slice(6)}`;
    }
    return phone;
  };

  // Load Plivo SDK
  const loadPlivoSDK = () => {
    return new Promise((resolve, reject) => {
      if (window.Plivo) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = PLIVO_SDK_URL;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Plivo SDK'));
      document.body.appendChild(script);
    });
  };

  // Initialize Plivo and connect
  const initializePlivo = async () => {
    setPhase('connecting');
    setError(null);

    try {
      await loadPlivoSDK();

      // Get credentials
      const credentials = await apiService.get('/amn/calling/token/');

      console.log('[Plivo] SDK loaded, creating client...');
      console.log('[Plivo] Credentials:', { username: credentials.username, hasPassword: !!credentials.password });

      const plivoOptions = {
        debug: 'ALL',
        permOnClick: true,
        enableTracking: false,
        closeProtection: false,
        maxAverageBitrate: 48000,
      };

      const plivoClient = new window.Plivo(plivoOptions);
      plivoRef.current = plivoClient;

      // Event handlers
      plivoClient.client.on('onWebrtcNotSupported', () => {
        console.error('[Plivo] WebRTC not supported');
        setError('WebRTC not supported in this browser');
        setPhase('confirm');
      });

      plivoClient.client.on('onLogin', () => {
        console.log('[Plivo] Login SUCCESS');
        setSdkConnected(true);
        setPhase('ready');
      });

      plivoClient.client.on('onLoginFailed', (cause) => {
        console.error('[Plivo] Login FAILED:', cause);
        setError(`Connection failed: ${cause || 'Unknown error'}`);
        setPhase('confirm');
      });

      plivoClient.client.on('onConnectionChange', (event) => {
        console.log('[Plivo] Connection change:', JSON.stringify(event));
      });

      plivoClient.client.on('onCallRemoteRinging', () => {
        setCallStatus('ringing');
      });

      plivoClient.client.on('onCallAnswered', () => {
        setCallStatus('answered');
      });

      plivoClient.client.on('onCallTerminated', () => {
        setCallStatus('completed');
        setPhase('ended');
      });

      plivoClient.client.on('onCallFailed', (cause) => {
        // Map Plivo causes to user-friendly messages
        const causeMessages = {
          'Canceled': 'declined',
          'USER_BUSY': 'busy',
          'NO_ANSWER': 'no_answer',
          'CALL_REJECTED': 'declined',
          'BUSY': 'busy',
        };
        const mappedStatus = causeMessages[cause] || 'failed';
        setCallStatus(mappedStatus);
        setPhase('ended');
      });

      plivoClient.client.on('onMediaPermission', (isAllowed) => {
        if (!isAllowed) {
          setError('Microphone permission denied');
          setPhase('confirm');
        }
      });

      // Login — override navigator.onLine if browser reports false incorrectly
      // (known Chrome bug with VPNs/proxies; Plivo SDK checks this and refuses to connect)
      console.log('[Plivo] navigator.onLine:', navigator.onLine);
      if (!navigator.onLine) {
        console.warn('[Plivo] Browser reports offline but page loaded — overriding navigator.onLine');
        Object.defineProperty(navigator, 'onLine', { get: () => true, configurable: true });
      }
      console.log('[Plivo] Attempting login...');
      plivoClient.client.login(credentials.username, credentials.password);

    } catch (err) {
      setError(err.message || 'Failed to initialize calling');
      setPhase('confirm');
    }
  };

  // Start the call
  const startCall = async () => {
    if (!plivoRef.current || !sdkConnected) return;

    const digits = phoneNumber.replace(/\D/g, '');
    const formattedNumber = digits.startsWith('1') ? `+${digits}` : `+1${digits}`;

    setPhase('calling');
    setCallStatus('initiated');

    // Register call with backend to link to person
    try {
      const registerData = { to_number: formattedNumber };
      if (personId) {
        registerData.person_id = personId;
      }
      const result = await apiService.post('/amn/calling/register/', registerData);
      setRegisteredCallId(result.call_id);

      const extraHeaders = {
        'X-PH-to_number': formattedNumber,
        'X-PH-call_id': String(result.call_id),
      };

      plivoRef.current.client.call(formattedNumber, extraHeaders);
    } catch (err) {
      console.error('Failed to register call:', err);
      // Still make the call even if registration fails
      const extraHeaders = {
        'X-PH-to_number': formattedNumber,
      };
      plivoRef.current.client.call(formattedNumber, extraHeaders);
    }
  };

  // Hang up
  const hangUp = () => {
    if (plivoRef.current) {
      try {
        plivoRef.current.client.hangup();
      } catch (e) {
        // Ignore hangup errors
      }
    }
    setPhase('ended');
    setCallStatus('completed');
  };

  // Toggle mute
  const toggleMute = () => {
    if (plivoRef.current && phase === 'calling') {
      if (isMuted) {
        plivoRef.current.client.unmute();
      } else {
        plivoRef.current.client.mute();
      }
      setIsMuted(!isMuted);
    }
  };

  // Cleanup on close
  const handleClose = () => {
    if (plivoRef.current) {
      try {
        if (phase === 'calling') {
          plivoRef.current.client.hangup();
        }
        plivoRef.current.client.logout();
      } catch (e) {
        // Ignore cleanup errors
      }
    }
    plivoRef.current = null;
    setPhase('confirm');
    setCallStatus(null);
    setError(null);
    setSdkConnected(false);
    setIsMuted(false);
    setRegisteredCallId(null);
    onClose();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (plivoRef.current) {
        try {
          plivoRef.current.client.logout();
        } catch (e) {
          // Ignore
        }
      }
    };
  }, []);

  if (!isOpen) return null;

  const getStatusColor = () => {
    switch (callStatus) {
      case 'initiated': return 'text-yellow-600';
      case 'ringing': return 'text-blue-600';
      case 'answered': return 'text-green-600';
      case 'completed': return 'text-gray-600';
      case 'declined': return 'text-orange-600';
      case 'busy': return 'text-orange-600';
      case 'no_answer': return 'text-orange-600';
      case 'failed': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusText = () => {
    switch (callStatus) {
      case 'initiated': return 'Connecting...';
      case 'ringing': return 'Ringing...';
      case 'answered': return 'Connected';
      case 'completed': return 'Call ended';
      case 'declined': return 'Call declined';
      case 'busy': return 'Line busy';
      case 'no_answer': return 'No answer';
      case 'failed': return 'Call failed';
      default: return '';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={phase !== 'calling' ? handleClose : undefined} />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">
            {phase === 'calling' ? 'Call in Progress' : 'Make a Call'}
          </h3>
          {phase !== 'calling' && (
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Contact Info */}
          <div className="text-center mb-4">
            <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-gray-100 flex items-center justify-center">
              <Phone className="w-8 h-8 text-gray-400" />
            </div>
            {personName && (
              <p className="text-sm font-medium text-gray-900">{personName}</p>
            )}
            <p className="text-sm text-gray-600">{formatDisplayPhone(phoneNumber)}</p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 text-center">
              {error}
            </div>
          )}

          {/* Status */}
          {callStatus && (
            <div className={`mb-4 text-center text-sm font-medium ${getStatusColor()}`}>
              {getStatusText()}
            </div>
          )}

          {/* Connection Status */}
          {phase === 'connecting' && (
            <div className="flex items-center justify-center gap-2 mb-4 text-sm text-gray-600">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Connecting to service...</span>
            </div>
          )}

          {phase === 'ready' && (
            <div className="flex items-center justify-center gap-2 mb-4 text-sm text-green-600">
              <Wifi className="w-4 h-4" />
              <span>Ready to call</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-center gap-3">
            {phase === 'confirm' && (
              <>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={initializePlivo}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Phone className="w-4 h-4" />
                  Call
                </button>
              </>
            )}

            {phase === 'ready' && (
              <>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={startCall}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Phone className="w-4 h-4" />
                  Start Call
                </button>
              </>
            )}

            {phase === 'calling' && (
              <>
                <button
                  onClick={toggleMute}
                  className={`p-3 rounded-full transition-colors ${
                    isMuted
                      ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </button>
                <button
                  onClick={hangUp}
                  className="p-3 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                  title="Hang up"
                >
                  <PhoneOff className="w-5 h-5" />
                </button>
              </>
            )}

            {phase === 'ended' && (
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CallModal;
